/**
 * Hook de Gerenciamento de Consentimento LGPD
 * Integra o ConsentBanner com bloqueio real de cookies/scripts
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocalStorage } from './useLocalStorage';

export interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
  timestamp: number;
  version: string;
}

interface ConsentManagerConfig {
  version?: string;
  expiryDays?: number;
  onConsentChange?: (preferences: ConsentPreferences) => void;
}

interface LoadedScript {
  id: string;
  src: string;
  category: keyof Omit<ConsentPreferences, 'timestamp' | 'version'>;
}

const CONSENT_VERSION = '1.0.0';
const CONSENT_KEY = 'johnny-vaz-lgpd-consent';
const LOADED_SCRIPTS_KEY = 'johnny-vaz-loaded-scripts';

const DEFAULT_PREFERENCES: ConsentPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  personalization: false,
  timestamp: Date.now(),
  version: CONSENT_VERSION,
};

// Lista de cookies por categoria para limpeza
const COOKIE_PATTERNS: Record<string, RegExp[]> = {
  analytics: [
    /^_ga/,
    /^_gid/,
    /^_gat/,
    /^__utm/,
    /^_hjid/,
    /^_hjSession/,
    /^mp_/,
    /^amplitude/,
  ],
  marketing: [
    /^_fbp/,
    /^_fbc/,
    /^fr$/,
    /^_gcl_/,
    /^_uet/,
    /^li_/,
    /^_pin_/,
  ],
  personalization: [
    /^_pref/,
    /^theme/,
    /^lang/,
  ],
};

export function useConsentManager(config: ConsentManagerConfig = {}) {
  const {
    version = CONSENT_VERSION,
    expiryDays = 365,
    onConsentChange,
  } = config;

  const [preferences, setPreferences] = useLocalStorage<ConsentPreferences | null>(
    CONSENT_KEY,
    null
  );
  const [loadedScripts, setLoadedScripts] = useLocalStorage<LoadedScript[]>(
    LOADED_SCRIPTS_KEY,
    []
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const needsConsent = !preferences ||
      preferences.version !== version ||
      Date.now() - preferences.timestamp > (expiryDays * 24 * 60 * 60 * 1000);

    setShowBanner(needsConsent);
    setIsInitialized(true);

    if (needsConsent) {
      console.log('[LGPD] Consentimento necessário');
    } else {
      console.log('[LGPD] Consentimento válido:', preferences);
    }
  }, [preferences, version, expiryDays]);

  useEffect(() => {
    if (preferences && isInitialized) {
      onConsentChange?.(preferences);
      applyConsentRestrictions(preferences);
    }
  }, [preferences, isInitialized, onConsentChange]);

  const applyConsentRestrictions = useCallback((prefs: ConsentPreferences) => {
    if (typeof window !== 'undefined') {
      if (!prefs.analytics) {
        (window as any)['ga-disable-GA_MEASUREMENT_ID'] = true;
        clearCookiesByCategory('analytics');
        console.log('[LGPD] Analytics desabilitado');
      } else {
        (window as any)['ga-disable-GA_MEASUREMENT_ID'] = false;
        console.log('[LGPD] Analytics habilitado');
      }

      if (!prefs.marketing) {
        clearCookiesByCategory('marketing');
        if ((window as any).fbq) {
          (window as any).fbq('consent', 'revoke');
        }
        console.log('[LGPD] Marketing desabilitado');
      } else {
        if ((window as any).fbq) {
          (window as any).fbq('consent', 'grant');
        }
        console.log('[LGPD] Marketing habilitado');
      }

      if (!prefs.personalization) {
        clearCookiesByCategory('personalization');
        console.log('[LGPD] Personalização desabilitada');
      }
    }
  }, []);

  const clearCookiesByCategory = useCallback((category: string) => {
    if (typeof document === 'undefined') return;

    const patterns = COOKIE_PATTERNS[category] || [];
    const cookies = document.cookie.split(';');

    cookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      const shouldDelete = patterns.some(pattern => pattern.test(cookieName));

      if (shouldDelete) {
        const domains = [
          window.location.hostname,
          '.' + window.location.hostname,
          window.location.hostname.split('.').slice(-2).join('.'),
          '.' + window.location.hostname.split('.').slice(-2).join('.'),
        ];

        const paths = ['/', ''];

        domains.forEach(domain => {
          paths.forEach(path => {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
          });
        });

        console.log(`[LGPD] Cookie removido: ${cookieName}`);
      }
    });
  }, []);

  const hasConsent = useCallback((category: keyof Omit<ConsentPreferences, 'timestamp' | 'version'>): boolean => {
    if (!preferences) return category === 'necessary';
    return preferences[category] === true;
  }, [preferences]);

  const loadScript = useCallback((
    id: string,
    src: string,
    category: keyof Omit<ConsentPreferences, 'timestamp' | 'version'>,
    options: { async?: boolean; defer?: boolean; onLoad?: () => void } = {}
  ): boolean => {
    if (category !== 'necessary' && !hasConsent(category)) {
      console.log(`[LGPD] Script bloqueado (sem consentimento ${category}):`, src);
      return false;
    }

    if (document.getElementById(id)) {
      console.log(`[LGPD] Script já carregado:`, id);
      return true;
    }

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = options.async ?? true;
    script.defer = options.defer ?? false;

    if (options.onLoad) {
      script.onload = options.onLoad;
    }

    document.head.appendChild(script);
    setLoadedScripts(prev => [...prev, { id, src, category }]);

    console.log(`[LGPD] Script carregado (${category}):`, src);
    return true;
  }, [hasConsent, setLoadedScripts]);

  const removeScriptsByCategory = useCallback((
    category: keyof Omit<ConsentPreferences, 'timestamp' | 'version'>
  ) => {
    loadedScripts
      .filter(s => s.category === category)
      .forEach(script => {
        const element = document.getElementById(script.id);
        if (element) {
          element.remove();
          console.log(`[LGPD] Script removido:`, script.id);
        }
      });

    setLoadedScripts(prev => prev.filter(s => s.category !== category));
  }, [loadedScripts, setLoadedScripts]);

  const savePreferences = useCallback((newPreferences: Partial<ConsentPreferences>) => {
    const updatedPreferences: ConsentPreferences = {
      ...DEFAULT_PREFERENCES,
      ...preferences,
      ...newPreferences,
      necessary: true,
      timestamp: Date.now(),
      version,
    };

    setPreferences(updatedPreferences);
    setShowBanner(false);

    console.log('[LGPD] Consentimento salvo:', {
      ...updatedPreferences,
      userAgent: navigator.userAgent,
      url: window.location.href,
    });

    applyConsentRestrictions(updatedPreferences);

    if (!updatedPreferences.analytics) {
      removeScriptsByCategory('analytics');
    }
    if (!updatedPreferences.marketing) {
      removeScriptsByCategory('marketing');
    }
    if (!updatedPreferences.personalization) {
      removeScriptsByCategory('personalization');
    }

    return updatedPreferences;
  }, [preferences, version, setPreferences, applyConsentRestrictions, removeScriptsByCategory]);

  const acceptAll = useCallback(() => {
    return savePreferences({
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
    });
  }, [savePreferences]);

  const acceptNecessaryOnly = useCallback(() => {
    return savePreferences({
      necessary: true,
      analytics: false,
      marketing: false,
      personalization: false,
    });
  }, [savePreferences]);

  const revokeAll = useCallback(() => {
    clearCookiesByCategory('analytics');
    clearCookiesByCategory('marketing');
    clearCookiesByCategory('personalization');

    removeScriptsByCategory('analytics');
    removeScriptsByCategory('marketing');
    removeScriptsByCategory('personalization');

    setPreferences(null);
    setShowBanner(true);

    console.log('[LGPD] Consentimento revogado');
  }, [clearCookiesByCategory, removeScriptsByCategory, setPreferences]);

  const exportConsentData = useCallback(() => {
    const data = {
      preferences,
      loadedScripts,
      exportedAt: new Date().toISOString(),
      version,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lgpd-consent-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);

    return data;
  }, [preferences, loadedScripts, version]);

  const consentStatus = useMemo(() => ({
    hasAnalytics: hasConsent('analytics'),
    hasMarketing: hasConsent('marketing'),
    hasPersonalization: hasConsent('personalization'),
    isConsentGiven: preferences !== null,
  }), [preferences, hasConsent]);

  return {
    preferences,
    showBanner,
    isInitialized,
    consentStatus,
    hasConsent,
    savePreferences,
    acceptAll,
    acceptNecessaryOnly,
    revokeAll,
    loadScript,
    removeScriptsByCategory,
    exportConsentData,
    clearCookiesByCategory,
  };
}

export default useConsentManager;
